import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, ShieldCheck, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/auth";
import { FORMS } from "../constants/testIds";
import { toast } from "sonner";

export default function SupportPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "patient",
    category: "general",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/forms/support-request", form);
      setSent(true);
      toast.success("Your request has been received by Bonded Thru Cancer.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "We could not submit your request. Please try again or email hello@bondedthrucancer.org.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white">
      <Header />
      <main id="main-content" className="pt-28 pb-16">
        <section className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <p className="btc-eyebrow mb-4">GET SUPPORT</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0B132B] leading-[1.05] mb-6">
                We're here.
                <br />
                <span className="text-[#23803F]">For every step.</span>
              </h1>
              <p className="text-lg text-[#4B5563] mb-8">
                Tell us where you are in your journey and what you need. A real BTC team member will
                read every message. We do our best to respond quickly, though response times vary
                with volume.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Heart, t: "Patient navigation", d: "Personalised guidance for newly diagnosed patients." },
                  { icon: ShieldCheck, t: "Carer support", d: "Practical tools and emotional support for families." },
                  { icon: PhoneCall, t: "24/7 Support Line", d: "Trained listeners available any hour, every day." },
                ].map((s) => (
                  <div key={s.t} className="flex gap-4 items-start">
                    <div className="w-11 h-11 rounded-xl bg-[#2D9B4E]/10 flex items-center justify-center shrink-0">
                      <s.icon className="w-5 h-5 text-[#23803F]" />
                    </div>
                    <div>
                      <div className="font-bold text-[#0B132B]">{s.t}</div>
                      <div className="text-sm text-[#4B5563]">{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#F8F9FB] rounded-3xl p-8 lg:p-10 border border-gray-100">
              {sent ? (
                <div className="text-center py-8" data-testid="support-form-success">
                  <Heart className="w-12 h-12 text-[#23803F] mx-auto mb-4" fill="#2D9B4E" />
                  <h3 className="text-2xl font-bold text-[#0B132B] mb-2">Thank you.</h3>
                  <p className="text-[#4B5563] mb-6">Your request has been received by Bonded Thru Cancer. Someone from our team will read it and reach out.</p>
                  <Link to="/" className="btc-btn-outline">Back to home</Link>
                </div>
              ) : (
                <form data-testid={FORMS.supportForm} onSubmit={onSubmit} className="space-y-4" aria-label="Contact BTC support">
                  <h2 className="text-xl font-bold text-[#0B132B] mb-2">Send us a message</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="support-name" className="sr-only">Your name</label>
                      <input id="support-name" data-testid={FORMS.supportName} required autoComplete="name" placeholder="Your name" value={form.name} onChange={setField("name")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30" />
                    </div>
                    <div>
                      <label htmlFor="support-email" className="sr-only">Your email</label>
                      <input id="support-email" data-testid={FORMS.supportEmail} type="email" required autoComplete="email" placeholder="Email" value={form.email} onChange={setField("email")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="support-phone" className="sr-only">Phone (optional)</label>
                      <input id="support-phone" data-testid={FORMS.supportPhone} autoComplete="tel" placeholder="Phone (optional)" value={form.phone} onChange={setField("phone")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30" />
                    </div>
                    <div>
                      <label htmlFor="support-role" className="sr-only">Who you are</label>
                      <select id="support-role" value={form.role} onChange={setField("role")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30">
                        <option value="patient">I am a patient</option>
                        <option value="caregiver">Carer</option>
                        <option value="survivor">Survivor</option>
                        <option value="family">Family member</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="support-category" className="sr-only">Type of support</label>
                    <select id="support-category" value={form.category} onChange={setField("category")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30">
                      <option value="general">General support</option>
                      <option value="financial">Financial assistance</option>
                      <option value="emotional">Emotional support</option>
                      <option value="clinical">Clinical questions</option>
                      <option value="volunteer">Volunteer / community</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="support-message" className="sr-only">How can we help?</label>
                    <textarea id="support-message" data-testid={FORMS.supportMessage} required placeholder="How can we help?" rows={5} value={form.message} onChange={setField("message")} className="w-full px-4 py-3 rounded-xl bg-white border border-gray-200 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 resize-none" />
                  </div>
                  <button data-testid={FORMS.supportSubmit} type="submit" disabled={busy} className="w-full btc-btn-primary justify-center disabled:opacity-60">
                    {busy ? "Sending..." : "Send message"}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
